import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Typography,
  Chip
} from '@mui/material';
import useAppStore from '../store/useAppStore';
import { scheduleAPI } from '../services/apiWithAuth';

const CellEditor = ({ open, cellData, onClose, onSave, onDelete }) => {
  const { workTypes, setNotification } = useAppStore();
  
  const [workTypeCode, setWorkTypeCode] = useState('');
  const [customText, setCustomText] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open && cellData) {
      // Blokada edycji weekendów
      if (cellData.isWeekend) {
        setNotification({
          type: 'warning',
          message: 'Nie można edytować harmonogramu w weekendy (soboty i niedziele)'
        });
        onClose();
        return;
      }
      
      setWorkTypeCode(cellData.existing?.work_type_code || cellData.existing?.work_type || 'B');
      setCustomText(cellData.existing?.custom_text || '');
    }
  }, [open, cellData, onClose, setNotification]);
  
  const handleSave = async () => {
    if (!cellData) return;
    
    setLoading(true);
    try {
      const updatedCellData = {
        ...cellData,
        work_type_code: workTypeCode,
        custom_text: customText.trim() || null
      };
      
      await onSave(updatedCellData);
      onClose();
    } catch (error) {
      console.error('Error saving cell:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas zapisywania harmonogramu'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!cellData || !cellData.existing) return;
    
    setLoading(true);
    try {
      await onDelete(cellData);
      onClose();
    } catch (error) {
      console.error('Error deleting cell:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas usuwania wpisu'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const selectedWorkType = workTypes.find(wt => wt.code === workTypeCode);
  
  if (!cellData) return null;
  
  const employee = cellData.employee_code;
  const day = cellData.day;
  const date = cellData.date;
  const isWeekend = cellData.isWeekend;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">
            Edycja harmonogramu
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {employee} - {day}/{date.getMonth() + 1}/{date.getFullYear()}
            {isWeekend && (
              <Chip 
                label="WEEKEND" 
                size="small" 
                color="warning" 
                sx={{ ml: 1 }} 
              />
            )}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {isWeekend ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body1" color="warning.main">
              ⚠️ Nie można edytować harmonogramu w weekendy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Soboty i niedziele są zablokowane do edycji.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          
          {/* Work Type Selection */}
          <FormControl fullWidth>
            <InputLabel>Typ pracy</InputLabel>
            <Select
              value={workTypeCode}
              label="Typ pracy"
              onChange={(e) => setWorkTypeCode(e.target.value)}
            >
              {workTypes.map((workType) => (
                <MenuItem key={workType.code} value={workType.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={workType.code}
                      sx={{
                        backgroundColor: workType.color,
                        color: 'white',
                        minWidth: 40
                      }}
                    />
                    {workType.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Preview of selected work type */}
          {selectedWorkType && (
            <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Wybrany typ:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={selectedWorkType.code}
                  sx={{
                    backgroundColor: selectedWorkType.color,
                    color: 'white'
                  }}
                />
                <Typography variant="body1">
                  {selectedWorkType.label}
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Custom Text (e.g., hours) */}
          <TextField
            fullWidth
            label="Tekst niestandardowy (np. godziny)"
            placeholder="np. 8:00-16:00"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            helperText="Opcjonalny tekst dodatkowy, np. godziny pracy"
            multiline
            maxRows={3}
          />
          
        </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {isWeekend ? 'Zamknij' : 'Anuluj'}
        </Button>
        
        {!isWeekend && cellData.existing && (
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={loading}
          >
            Usuń
          </Button>
        )}
        
        {!isWeekend && (
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CellEditor;